import { useEffect, useState } from "react";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faCheck, faXmark } from "@fortawesome/pro-solid-svg-icons";
import RequiredReactSelect from "@components/FormsV2/RequiredReactSelect/RequiredReactSelect";
import type { LobApprovalUserRow } from "../types/LobApproval";
import { useLobApprovalUserMutations } from "../api/useLobApprovalUserMutations";
import "./style.css";

export interface AvailableUser {
  id: string;
  name: string;
}

export interface ApproversSectionProps {
  lobApprovalId: string;
  approvers: LobApprovalUserRow[];
  availableUsers: AvailableUser[];
  onChanged: () => void;
}

// Local row shape. New rows have `id` starting with "new-".
interface ApproverRow {
  id: string;
  userId: string | null;
  name: string;
  requiredApprover: boolean;
  isNew: boolean;
  isChanged: boolean;
}

const isNewId = (id: string) => id.startsWith("new-");

export const ApproversSection = ({
  lobApprovalId,
  approvers,
  availableUsers,
  onChanged,
}: ApproversSectionProps) => {
  const { create, update, destroy } = useLobApprovalUserMutations();
  const [rows, setRows] = useState<ApproverRow[]>([]);
  const [error, setError] = useState<string | null>(null);

  // Reseed local rows whenever the upstream approvers prop changes. New (unsaved)
  // rows are dropped here because the parent only knows about persisted records.
  useEffect(() => {
    setRows(
      approvers.map((a) => ({
        id: a.id,
        userId: a.userId,
        name: a.name,
        requiredApprover: a.requiredApprover,
        isNew: false,
        isChanged: false,
      }))
    );
  }, [approvers]);

  const usedUserIds = new Set(
    rows.map((r) => r.userId).filter((u): u is string => u != null && u !== "")
  );
  const remainingUsers = availableUsers.filter((u) => !usedUserIds.has(u.id));

  const patchRow = (id: string, patch: Partial<ApproverRow>) =>
    setRows((prev) =>
      prev.map((r) => (r.id === id ? { ...r, ...patch, isChanged: true } : r))
    );

  const addRow = () => {
    const id = `new-${Date.now()}`;
    setRows((prev) => [
      ...prev,
      {
        id,
        userId: null,
        name: "",
        requiredApprover: false,
        isNew: true,
        isChanged: true,
      },
    ]);
  };

  const saveRow = async (row: ApproverRow) => {
    setError(null);
    try {
      if (row.isNew) {
        if (!row.userId) {
          setError("Pick an approver before saving.");
          return;
        }
        await create(lobApprovalId, row.userId, row.requiredApprover);
      } else if (row.isChanged) {
        await update(row.id, row.requiredApprover);
      }
      onChanged();
    } catch {
      setError(
        row.isNew ? "Failed to add approver." : "Failed to update approver."
      );
    }
  };

  const removeRow = async (row: ApproverRow) => {
    setError(null);
    if (row.isNew) {
      // Unsaved — just drop from local state.
      setRows((prev) => prev.filter((r) => r.id !== row.id));
      return;
    }
    try {
      await destroy(row.id);
      onChanged();
    } catch {
      setError("Failed to remove approver.");
    }
  };

  return (
    <div className="mt-4">
      <h4>Project Technical Approval</h4>

      {error && <div className="alert alert-danger">{error}</div>}

      {rows.length === 0 ? (
        <p className="text-muted">No approvers assigned yet.</p>
      ) : (
        <table className="approversTable">
          <thead>
            <tr>
              <th>Approver</th>
              <th className="approversTable__required">Required?</th>
              <th className="approversTable__actions" />
            </tr>
          </thead>
          <tbody>
            {rows.map((row) => {
              const userOptions = remainingUsers
                .concat(
                  row.userId && !remainingUsers.some((u) => u.id === row.userId)
                    ? availableUsers.filter((u) => u.id === row.userId)
                    : []
                )
                .map((u) => ({ value: u.id, label: u.name }));
              return (
                <tr key={row.id}>
                  <td>
                    {row.isNew ? (
                      <RequiredReactSelect
                        label={null as any}
                        value={
                          row.userId
                            ? userOptions.find((o) => o.value === row.userId)
                            : undefined
                        }
                        onChange={(opt: any) => {
                          const userId = opt ? String(opt.value) : null;
                          const match = availableUsers.find(
                            (u) => u.id === userId
                          );
                          patchRow(row.id, {
                            userId,
                            name: match?.name ?? "",
                          });
                        }}
                        options={userOptions}
                        placeholder="Select an approver"
                      />
                    ) : (
                      <span>{row.name}</span>
                    )}
                  </td>
                  <td className="approversTable__required">
                    <input
                      type="checkbox"
                      className="approversTable__requiredCheck"
                      aria-label={`Required: ${row.name || "new approver"}`}
                      checked={row.requiredApprover}
                      onChange={(e) =>
                        patchRow(row.id, {
                          requiredApprover: e.target.checked,
                        })
                      }
                    />
                  </td>
                  <td className="approversTable__actions">
                    <FontAwesomeIcon
                      icon={faCheck}
                      className="approversTable__actionIcon approversTable__actionIcon--save"
                      role="button"
                      aria-label={`Save approver ${
                        row.name || "(unsaved row)"
                      }`}
                      tabIndex={0}
                      onClick={() => saveRow(row)}
                      onKeyDown={(e) => {
                        if (e.key === "Enter" || e.key === " ") saveRow(row);
                      }}
                    />
                    <FontAwesomeIcon
                      icon={faXmark}
                      className="approversTable__actionIcon approversTable__actionIcon--remove"
                      role="button"
                      aria-label={`Remove approver ${
                        row.name || "(unsaved row)"
                      }`}
                      tabIndex={0}
                      onClick={() => removeRow(row)}
                      onKeyDown={(e) => {
                        if (e.key === "Enter" || e.key === " ") removeRow(row);
                      }}
                    />
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      )}

      <button
        type="button"
        className="approversTable__addLink"
        onClick={addRow}
      >
        Add an Approver +
      </button>
    </div>
  );
};
